// Раздел «Расчёт»: ведомость, табель и официальный контур выплат.
//
// Табель здесь, а не в «Объектах», по двум причинам сразу. Первая: у полевого руководителя нет
// права на справочники — до «Объектов» он не дотянется, а табель по своему объекту вести обязан.
// Вторая: сетка на тридцать один день физически не помещается в колонку детали (полоса разделов
// 240 плюс список 384 — на ноутбуке 1366 остаётся около 740, а нужно больше 1300).
//
// Раздел открывается по ЛЮБОМУ из трёх прав — на ведомости, на табель либо на официальный
// контур. С одним правом табельщик не увидел бы раздел вовсе и не нашёл бы собственную работу.
//
// Представление «Официально» показывается только тем, у кого есть право его видеть: официальная
// часть полевым ролям и охраннику не показывается вовсе, и вкладка, ведущая в отказ, — это тот
// же отказ, только на два нажатия позже.

// Полоса вкладок, а не сегментированный переключатель. Правка 03.09.2026, общая на весь продукт.
//
// Сегментированный раскладывает доли РАВНЫМИ и запрещает перенос текста: как только пунктов
// становится больше, чем помещается, названия налезают друг на друга. В «Найме» это уже
// случилось — восемь названий в колонке 416 точек рисовались одно поверх другого, — а в
// «Деньгах» стояло в шаге от этого: «Очередь приёмки» требовала 115 точек при доле в 106.
//
// Граница теперь по смыслу, а не по числу пунктов: сегментированный остаётся там, где человек
// выбирает РЕЖИМ из двух-трёх равных (порядок сортировки на пульте, способ ведения табеля);
// переход между ПРЕДСТАВЛЕНИЯМИ раздела везде идёт полосой вкладок. У неё ширина по
// содержимому и прокрутка, то есть наложение невозможно по устройству. Держит
// `e2e/переключатель-читается.spec.ts` — замером координат, а не чтением исходника: пункты
// собираются циклом по правам, и в тексте файла их не пересчитать.

import { useRef } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import { useAuthStore } from '@/shared/auth/store';
import { Подсказка } from '@/shared/hints/Подсказка';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, parseSearch } from '@/shared/routing/search';
import * as TabMenu from '@/shared/ui/tab-menu-horizontal';

// Побочный эффект: раздел объявляет каркасу свои панели.
import './debts/panels/register';
import './official/panels/register';
import './sheets/panels/register';
import './timesheet/register';

import { DebtsScreen } from './debts/DebtsScreen';
import { OfficialScreen } from './official/OfficialScreen';
import { DeductionsScreen } from './deductions/DeductionsScreen';
import {
  VIEW_DEBTS,
  VIEW_DEDUCTIONS,
  VIEW_OFFICIAL,
  VIEW_SETTLEMENT,
  VIEW_SHEETS,
  VIEW_SUMMARY,
  VIEW_TIMESHEET,
} from './route';
import { SettlementScreen } from './settlement/SettlementScreen';
import { SheetsScreen } from './sheets/SheetsScreen';
import { SummaryScreen } from './summary/SummaryScreen';
import { TimesheetScreen } from './timesheet/TimesheetScreen';

export function PayrollSection({ view: viewFromShell }: { view?: string }) {
  const params = useParams<{ view?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const view = params.view ?? viewFromShell;
  const search = parseSearch(location.search);

  function поставитьОтбор(key: string, value: string | null) {
    const фильтры = { ...search.filters };
    if (value === null) {
      delete фильтры[key];
    } else {
      фильтры[key] = value;
    }
    void navigate({
      pathname: location.pathname,
      search: buildSearch({ ...search, filters: фильтры, panels: [] }),
    });
  }

  if (view === VIEW_OFFICIAL) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_OFFICIAL} />
        <OfficialScreen search={search} />
      </div>
    );
  }

  if (view === VIEW_SUMMARY) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_SUMMARY} />
        <SummaryScreen search={search} />
      </div>
    );
  }

  if (view === VIEW_DEBTS) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_DEBTS} />
        <DebtsScreen search={search} />
      </div>
    );
  }

  if (view === VIEW_DEDUCTIONS) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_DEDUCTIONS} />
        <DeductionsScreen search={search} />
      </div>
    );
  }

  if (view === VIEW_SETTLEMENT) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_SETTLEMENT} />
        <SettlementScreen search={search} />
      </div>
    );
  }

  if (view === VIEW_TIMESHEET) {
    return (
      <div className='flex h-full min-w-0 flex-1 flex-col'>
        <ПереключательПредставлений текущее={VIEW_TIMESHEET} />
        <TimesheetScreen search={search} onFilter={поставитьОтбор} />
      </div>
    );
  }

  return (
    <div className='flex h-full min-w-0 flex-1 flex-col'>
      <ПереключательПредставлений текущее={VIEW_SHEETS} />
      <SheetsScreen search={search} />
    </div>
  );
}

/**
 * Переключатель представлений раздела.
 *
 * Отбор при переходе НЕ переносится: у табеля он месяц и объект, у контура — период с 15 по 15
 * и состояние строки. Перенесённый чужой отбор дал бы пустой экран без объяснения, почему.
 * Официальный контур виден только тем, у кого есть право: полевым ролям и охраннику
 * официальная часть не показывается вовсе.
 */
function ПереключательПредставлений({ текущее }: { текущее: string }) {
  const navigate = useNavigate();
  const права = useAuthStore((state) => state.permissions);
  // Каждая вкладка — по своему праву. Вкладка, ведущая в отказ, — это тот же отказ, только на
  // два нажатия позже: табельщик без ведомостей не должен видеть «Ведомости».
  const ведомости = can(права, 'core.payroll.view');
  const табель = can(права, 'core.timesheet.view');
  const контур = can(права, 'core.official.view');
  // Сводка живёт по праву на панели показателей: её видят и те, у кого нет ведомостей
  // (табельщик видит недобор), но суммы внутри всё равно приходят по своему праву.
  const сводка = can(права, 'core.dashboard.view');
  // Реестр долгов — по праву на долги: табельщику тз даёт их на просмотр, охраннику нет.
  const долги = can(права, 'core.debt.view');
  // Удержания — те же права, что у сводки по ведомостям на сервере: это суммы тех же ведомостей.
  const удержания = can(права, 'core.payroll.view') && can(права, 'core.report.view');
  // Расчёт сотрудника собирается из ведомостей и открыт тем же правом, что и они. Рядом с
  // «Удержаниями» стоит потому, что отвечает на тот же вопрос в другом разрезе: не «сколько
  // удержано за месяц у всех», а «сколько всего за все месяцы у одного».
  const расчётСотрудника = can(права, 'core.payroll.view');
  // Подсказка первого раза стоит на самой вкладке: заказчик просил «строку удержания», и найти
  // её он должен, ещё не зайдя внутрь.
  const якорьУдержаний = useRef<HTMLButtonElement>(null);

  return (
    // Поле 16 в узком окне: полоса представлений стоит в той же колонке, что список,
    // карточка и шапка отборов, и разные поля читаются как дрожание текста при переходе.
    <div className='flex items-center gap-2 border-b border-stroke-soft-200 px-5 py-2 узко:px-4'>
      <TabMenu.Root
        value={текущее}
        onValueChange={(следующее) => {
          if (следующее !== текущее) {
            void navigate(`/payroll/${следующее}`);
          }
        }}
      >
        <TabMenu.List className='h-10 gap-5 border-t-0'>
          {ведомости && <TabMenu.Trigger value={VIEW_SHEETS}>Ведомости</TabMenu.Trigger>}
          {табель && <TabMenu.Trigger value={VIEW_TIMESHEET}>Табель</TabMenu.Trigger>}
          {контур && <TabMenu.Trigger value={VIEW_OFFICIAL}>Официально</TabMenu.Trigger>}
          {сводка && <TabMenu.Trigger value={VIEW_SUMMARY}>Сводка</TabMenu.Trigger>}
          {долги && <TabMenu.Trigger value={VIEW_DEBTS}>Долги</TabMenu.Trigger>}
          {удержания && (
            <TabMenu.Trigger ref={якорьУдержаний} value={VIEW_DEDUCTIONS}>
              Удержания
            </TabMenu.Trigger>
          )}
          {расчётСотрудника && (
            <TabMenu.Trigger value={VIEW_SETTLEMENT}>Расчёт сотрудника</TabMenu.Trigger>
          )}
        </TabMenu.List>
      </TabMenu.Root>
      {удержания && <Подсказка урок='удержания-за-месяц' якорь={якорьУдержаний} />}
    </div>
  );
}
